// Send a text or email (gap #3, office-history's "Text <customer>"): to one of the customer's phones
// or emails, with its consent said beside it, through messages.send. A refusal (no consent,
// do-not-contact) is shown in the api's own words; a company that can't text yet is told so first.
// In Messages it is a part of the page (SendMessage); on a customer's page it opens as a FormSheet
// (SendMessageSheet). BL-189: the fields are ChoiceGroup and TextField, the actions Button and Link.

import { useEffect, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { Button, Choice, ChoiceGroup, Link, TextField } from '@flowology/ui-web/fl';
import { FormSheet } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import type { Customer } from '../types.ts';
import { BODY_MAX, reachable } from './messages-model.ts';
import './messages-page.css';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

/** A message being written: who to, where it goes, its words, and what's still missing. */
function useDraft(customerId: string, to?: string) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [sms, setSms] = useState<{ ok: boolean; notSetUp: string } | null>(null);
  const [methodId, setMethodId] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    client
      .call<Customer>('customers.get', { id: customerId })
      .then((c) => {
        setCustomer(c);
        const ways = reachable(c);
        const start =
          ways.find((w) => to && w.method.value === to && w.sendable) ??
          ways.find((w) => w.sendable) ??
          ways[0];
        setMethodId(start?.method.id ?? null);
      })
      .catch((e: unknown) => setError(message(e)));
    client
      .call<{ sms: boolean; notSetUp: string }>('messages.channels', {})
      .then((r) => setSms({ ok: r.sms, notSetUp: r.notSetUp }))
      .catch(() => setSms(null));
  }, [customerId, to]);

  const ways = customer ? reachable(customer) : [];
  const way = ways.find((w) => w.method.id === methodId) ?? null;
  const email = way?.method.kind === 'email';
  const noTexting = !!way && !email && sms !== null && !sms.ok;
  const missing = !way
    ? 'Pick where it goes.'
    : !way.sendable
      ? way.chip.text === 'Do not contact'
        ? 'This customer asked not to be contacted.'
        : `${way.contactName} hasn’t agreed to ${email ? 'email' : 'texts'} at this address.`
      : noTexting
        ? sms!.notSetUp
        : email && !subject.trim()
          ? 'Add a subject.'
          : !body.trim()
            ? 'Write the message.'
            : null;

  /** Sends it; what was done, in words. Throws the api's refusal. */
  const send = async (): Promise<string> => {
    if (!way || missing) throw new Error(missing ?? 'Pick where it goes.');
    await client.call('messages.send', {
      contactMethodId: way.method.id,
      purpose: 'general',
      body: body.trim(),
      ...(email ? { subject: subject.trim() } : {}),
    });
    return `${email ? 'Email' : 'Text'} to ${way.contactName} is on its way.`;
  };

  const fields: ReactNode = (
    <>
      {!customer && !error && <p className="msgs__muted">Getting their phones and emails.</p>}
      {customer && !ways.length && (
        <p className="msgs__muted">
          There’s no phone or email on this customer yet. Add one under Contacts first.
        </p>
      )}
      {ways.length > 0 && (
        <ChoiceGroup
          label="To"
          selectionMode="single"
          layout="stack"
          value={methodId ?? ''}
          onChange={setMethodId}
        >
          {ways.map((w) => (
            <Choice key={w.method.id} value={w.method.id} description={w.chip.text}>
              {w.contactName} · {w.address}
            </Choice>
          ))}
        </ChoiceGroup>
      )}
      {email && <TextField label="Subject" value={subject} maxLength={200} onChange={setSubject} />}
      {way && (
        <TextField
          label="Message"
          multiline
          value={body}
          maxLength={BODY_MAX}
          onChange={setBody}
          description={`${body.length} of ${BODY_MAX.toLocaleString('en-CA')}`}
        />
      )}
    </>
  );
  return {
    customer,
    email,
    missing,
    send,
    fields,
    error,
    setError,
    isDirty: !!(subject.trim() || body.trim()),
  };
}

/** The message's name: who it's to, once they're known. */
export const draftTitle = (customer: Pick<Customer, 'displayName'> | null) =>
  customer ? `Write to ${customer.displayName}` : 'Write a message';

/** The composer as a part of a page: its fields, then Send and Cancel, then what's still missing. */
export function SendMessage({
  customerId,
  to,
  onSent,
  onCancel,
  onCustomer,
}: {
  customerId: string;
  /** Start on this address (a reply goes back where the message went). */
  to?: string;
  onSent: (what: string) => void;
  onCancel: () => void;
  /** The customer, once read: the page names its part after them. */
  onCustomer?: (c: Customer) => void;
}) {
  const d = useDraft(customerId, to);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (d.customer) onCustomer?.(d.customer);
  }, [d.customer]);
  const send = async () => {
    if (d.missing) return;
    setBusy(true);
    d.setError(null);
    try {
      onSent(await d.send());
    } catch (e) {
      d.setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="msgs__form">
      {d.fields}
      {d.error && (
        <p className="msgs__bad" role="alert">
          {d.error}
        </p>
      )}
      <div className="msgs__acts">
        <Button variant="primary" isBusy={busy} isDisabled={!busy && !!d.missing} onPress={() => void send()}>
          {d.email ? 'Send email' : 'Send text'}
        </Button>
        <Link onPress={onCancel}>Cancel</Link>
      </div>
      <p className="msgs__muted" role="status" aria-live="polite">
        {d.missing && !busy && d.customer ? d.missing : ''}
      </p>
    </div>
  );
}

/** "Send a text or email" on a customer's page: the same fields, as a FormSheet. */
export function SendMessageSheet({
  customerId,
  customerName,
  onSent,
  onClose,
}: {
  customerId: string;
  customerName: string;
  onSent: (what: string) => void;
  onClose: () => void;
}) {
  const d = useDraft(customerId);
  return (
    <FormSheet
      isOpen
      onClose={onClose}
      title={`Write to ${customerName}`}
      submitLabel={d.email ? 'Send email' : 'Send text'}
      isDirty={d.isDirty}
      isSubmitDisabled={!!d.missing}
      {...(d.missing && d.customer ? { note: d.missing } : {})}
      onSubmit={async () => {
        try {
          onSent(await d.send());
        } catch (e) {
          // the sheet says it in red, in the api's own words, and keeps what was written
          throw new Error(message(e), { cause: e });
        }
      }}
    >
      <div className="msgs__form">
        {d.fields}
        {d.error && (
          <p className="msgs__bad" role="alert">
            {d.error}
          </p>
        )}
      </div>
    </FormSheet>
  );
}
